import { cn } from "@/components/podkit/lib/cn";
import { type FC, type ReactNode, useRef, useCallback } from "react";

export type ShellInputProps = {
    /** The current value of the input */
    value: string;
    /** Callback when the value changes */
    onChange: (value: string) => void;
    /** Placeholder text when empty */
    placeholder?: string;
    /** Minimum height of the input area */
    minHeight?: number;
    /** Maximum height of the input area (will scroll beyond this) */
    maxHeight?: number;
    /** Whether the input is disabled */
    disabled?: boolean;
    /** Additional class name for the container */
    className?: string;
    /** The shell prompt symbol to show for each line (default: $) */
    shellSymbol?: ReactNode;
};

/**
 * A shell/command input component with line-by-line prompt symbols.
 * Designed for entering shell commands with a terminal-like appearance.
 * Lines do not wrap - long lines scroll horizontally.
 */
export const ShellInput: FC<ShellInputProps> = ({
    value,
    onChange,
    placeholder = "Enter shell commands",
    minHeight = 120,
    maxHeight = 400,
    disabled = false,
    className,
    shellSymbol = "$",
}) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const promptsRef = useRef<HTMLDivElement>(null);

    // Sync vertical scroll between textarea and prompts
    const handleScroll = useCallback(() => {
        if (textareaRef.current && promptsRef.current) {
            promptsRef.current.scrollTop = textareaRef.current.scrollTop;
        }
    }, []);

    // Calculate lines - normalize line endings and always show at least one line
    // Handle both \n (Unix) and \r\n (Windows) line endings
    const normalizedValue = (value || "").replace(/\r\n/g, "\n").replace(/\r/g, "\n");
    const lines = normalizedValue.split("\n");
    const lineCount = Math.max(lines.length, 1);

    return (
        <div
            className={cn(
                "flex overflow-hidden rounded-lg border border-border-base bg-surface-primary font-mono text-sm transition-colors",
                "focus-within:border-border-strong",
                disabled && "cursor-not-allowed opacity-50",
                className,
            )}
            style={{
                maxHeight: `${maxHeight}px`,
            }}
        >
            {/* Prompt column - syncs scroll with textarea */}
            <div
                ref={promptsRef}
                className="shrink-0 overflow-hidden border-r border-border-subtle pt-2 pb-2 select-none"
                aria-hidden="true"
            >
                {Array.from({ length: lineCount }).map((_, index) => {
                    const isEmpty = lines[index] === "";
                    return (
                        <div
                            key={index}
                            className={cn("px-3 leading-5", isEmpty ? "text-content-quaternary" : "text-content-tertiary")}
                        >
                            {shellSymbol}
                        </div>
                    );
                })}
            </div>

            {/* Textarea */}
            <textarea
                ref={textareaRef}
                className={cn(
                    "min-h-full flex-1 resize-none bg-transparent py-2 pl-3 pr-3 text-content-primary leading-5",
                    "outline-none focus:ring-0 placeholder:text-content-tertiary",
                    disabled && "cursor-not-allowed",
                )}
                style={{
                    minHeight: `${minHeight}px`,
                    whiteSpace: "pre",
                    overflowWrap: "normal",
                    wordBreak: "keep-all",
                }}
                wrap="off"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onScroll={handleScroll}
                placeholder={placeholder}
                disabled={disabled}
                spellCheck={false}
                autoCapitalize="off"
                autoCorrect="off"
            />
        </div>
    );
};
